
import { useEffect, useRef, useState } from 'react';

export default function ValueProposition() {
  const [isVisible, setIsVisible] = useState(false);
  const [counters, setCounters] = useState({ years: 0, industries: 0, potential: 0 });
  const [cardVisible, setCardVisible] = useState(false);
  const [ctaVisible, setCtaVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);
  const ctaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          const duration = 2400;
          const steps = 70;
          const interval = duration / steps;
          
          let step = 0;
          const timer = setInterval(() => {
            step++;
            const progress = step / steps;
            const ease = 1 - Math.pow(1 - progress, 3);
            setCounters({
              years: Math.floor(25 * ease),
              industries: Math.floor(6 * ease),
              potential: Math.floor(100 * ease)
            });
            if (step >= steps) clearInterval(timer);
          }, interval);
        }
      },
      { threshold: 0.12 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setCardVisible(true); },
      { threshold: 0.08, rootMargin: '0px 0px -50px 0px' }
    );
    if (cardsRef.current) observer.observe(cardsRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setCtaVisible(true); },
      { threshold: 0.12 }
    );
    if (ctaRef.current) observer.observe(ctaRef.current);
    return () => observer.disconnect();
  }, []);

  const values = [
    {
      number: '01',
      title: 'Instant Authority',
      description: 'Command immediate credibility in the intelligence and strategic sectors. This domain speaks power before a single word is read.',
      icon: 'ri-shield-star-line',
      highlight: 'Trust Factor'
    },
    {
      number: '02',
      title: 'Global Recognition',
      description: 'The .ORG extension paired with "International Intelligence" creates universal trust, memorability, and cross-border appeal.',
      icon: 'ri-earth-line',
      highlight: 'Worldwide Reach'
    },
    {
      number: '03',
      title: 'Investment Grade',
      description: 'Premium keyword combination with strong appreciation potential. Domain values in this category have shown consistent growth.',
      icon: 'ri-funds-box-line',
      highlight: 'Asset Value'
    },
    {
      number: '04',
      title: 'SEO Dominance',
      description: 'Two powerful keywords that naturally rank for high-value search queries across intelligence, consulting, and technology sectors.',
      icon: 'ri-search-line',
      highlight: 'Search Power'
    }
  ];

  return (
    <section ref={sectionRef} id="value" className="relative py-32 bg-[#faf9f7] overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-amber-50/80 to-transparent"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-amber-100/50 rounded-full blur-[150px]"></div>
      
      {/* Decorative Lines */}
      <div className="absolute top-32 left-0 w-32 h-px bg-gradient-to-r from-amber-500/30 to-transparent"></div>
      <div className="absolute bottom-32 right-0 w-32 h-px bg-gradient-to-l from-amber-500/30 to-transparent"></div>

      <div className="max-w-7xl mx-auto px-8 relative z-10">
        {/* Section Header */}
        <div className={`flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-20`}>
          <div
            className={`max-w-2xl transition-all duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-14'}`}
          >
            <span className="inline-flex items-center gap-2 text-[11px] tracking-[0.3em] text-amber-600/70 uppercase mb-6 font-mono">
              <i className="ri-sparkling-line"></i>
              Strategic Value
            </span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-light text-[#1a1a1a] leading-tight mb-6 font-display">
              Why This Domain<br />
              <span className="text-amber-600">Commands Attention</span>
            </h2>
            <p className="text-[#666] leading-relaxed font-body">
              In the digital landscape, your domain is your first impression. International-Intelligence.org delivers authority, trust, and memorability in a single address.
            </p>
          </div>

          {/* Stats Cards */}
          <div className="flex gap-4">
            {[
              { value: `${counters.years}+`, label: 'Years Value', delay: 300 },
              { value: `${counters.industries}+`, label: 'Industries', delay: 480 },
              { value: `${counters.potential}%`, label: 'Potential', delay: 660 },
            ].map((stat, i) => (
              <div
                key={i}
                className={`p-6 bg-white rounded-2xl border border-[#e5e5e5] shadow-sm min-w-[120px] text-center transition-all duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                style={{ transitionDelay: `${stat.delay}ms` }}
              >
                <div className="text-3xl font-light text-amber-600 mb-1 font-display">
                  {stat.value}
                </div>
                <div className="text-[10px] tracking-[0.15em] text-[#999] uppercase font-mono">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Timeline Value Grid */}
        <div ref={cardsRef} className="relative">
          {/* Vertical Timeline Line - Desktop */}
          <div className={`hidden lg:block absolute left-1/2 top-0 bottom-0 w-px transition-all duration-[1800ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${cardVisible ? 'opacity-100 scale-y-100' : 'opacity-0 scale-y-0'}`} style={{ transformOrigin: 'top', background: 'linear-gradient(to bottom, rgba(245,158,11,0.3), rgba(245,158,11,0.2), rgba(245,158,11,0.3))' }}></div>

          <div className="space-y-12 lg:space-y-0">
            {values.map((item, index) => (
              <div 
                key={index}
                className={`relative transition-all ease-[cubic-bezier(0.22,1,0.36,1)] ${cardVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'}`}
                style={{ transitionDuration: '1200ms', transitionDelay: `${450 + index * 250}ms` }}
              >
                <div className={`lg:grid lg:grid-cols-2 lg:gap-16 ${index % 2 === 1 ? 'lg:direction-rtl' : ''}`}>
                  {/* Content Side */}
                  <div className={`${index % 2 === 1 ? 'lg:col-start-2 lg:text-left' : 'lg:text-right'} mb-8 lg:mb-16`}>
                    {/* Card Container with Corner Shadow Box */}
                    <div className={`relative ${index % 2 === 1 ? '' : 'lg:ml-auto'} max-w-xl`}>
                      {/* Shadow Box Behind - Only Corners Visible */}
                      <div className="absolute -inset-3 pointer-events-none">
                        {/* Top Left Corner */}
                        <div className={`absolute top-0 left-0 w-6 h-6 border-t-2 border-l-2 border-amber-500/20 rounded-tl-lg transition-all duration-[1000ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${cardVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`} style={{ transitionDelay: `${700 + index * 250}ms` }}></div>
                        {/* Top Right Corner */}
                        <div className={`absolute top-0 right-0 w-6 h-6 border-t-2 border-r-2 border-amber-500/20 rounded-tr-lg transition-all duration-[1000ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${cardVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`} style={{ transitionDelay: `${850 + index * 250}ms` }}></div>
                        {/* Bottom Left Corner */}
                        <div className={`absolute bottom-0 left-0 w-6 h-6 border-b-2 border-l-2 border-amber-500/20 rounded-bl-lg transition-all duration-[1000ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${cardVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`} style={{ transitionDelay: `${850 + index * 250}ms` }}></div>
                        {/* Bottom Right Corner */}
                        <div className={`absolute bottom-0 right-0 w-6 h-6 border-b-2 border-r-2 border-amber-500/20 rounded-br-lg transition-all duration-[1000ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${cardVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-75'}`} style={{ transitionDelay: `${1000 + index * 250}ms` }}></div>
                      </div>

                      {/* Main Card */}
                      <div className="group relative p-8 bg-white rounded-2xl border border-[#e8e8e8] hover:border-amber-500/30 hover:shadow-xl hover:shadow-amber-500/5 transition-all duration-500">
                        {/* Card Header */}
                        <div className={`flex items-center gap-4 mb-6 ${index % 2 === 1 ? '' : 'lg:flex-row-reverse'}`}>
                          <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-br from-amber-500/10 to-amber-600/5 border border-amber-500/20 rounded-xl group-hover:border-amber-500/40 transition-colors">
                            <i className={`${item.icon} text-2xl text-amber-600`}></i>
                          </div>
                          <div className={index % 2 === 1 ? '' : 'lg:text-right'}>
                            <span className="text-[10px] tracking-[0.2em] text-amber-600/60 uppercase font-mini-title font-medium">{item.highlight}</span>
                            <h3 className="text-xl font-medium text-[#1a1a1a] font-mini-title">{item.title}</h3>
                          </div>
                        </div>
                        
                        {/* Card Body */}
                        <p className={`text-sm text-[#666] leading-relaxed font-body ${index % 2 === 1 ? '' : 'lg:text-right'}`}>
                          {item.description}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Number Side - Desktop */}
                  <div className={`hidden lg:flex items-center ${index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1 justify-end' : 'justify-start'}`}>
                    <div className="relative">
                      {/* Timeline Node */}
                      <div className={`absolute top-1/2 -translate-y-1/2 ${index % 2 === 1 ? '-right-[calc(50%+2rem)]' : '-left-[calc(50%+2rem)]'} w-4 h-4 bg-amber-500 rounded-full border-4 border-[#faf9f7] shadow-lg transition-all duration-[800ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${cardVisible ? 'scale-100 opacity-100' : 'scale-0 opacity-0'}`} style={{ transitionDelay: `${600 + index * 250}ms` }}></div>
                      
                      <span className={`text-8xl font-extralight text-amber-500/10 group-hover:text-amber-500/20 transition-all duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] select-none font-display ${cardVisible ? 'opacity-100 translate-x-0' : 'opacity-0'}`} style={{ transitionDelay: `${750 + index * 250}ms`, transform: cardVisible ? 'translateX(0)' : (index % 2 === 1 ? 'translateX(20px)' : 'translateX(-20px)') }}>
                        {item.number}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom CTA Card */}
        <div ref={ctaRef} className={`mt-24 transition-all duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${ctaVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-12 scale-[0.97]'}`}>
          {/* Outer Corner Frames */}
          <div className="relative">
            <div className="absolute -inset-4 pointer-events-none">
              <div className={`absolute top-0 left-0 w-10 h-10 border-t border-l border-amber-500/25 rounded-tl-xl transition-all duration-[1000ms] ${ctaVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}`} style={{ transitionDelay: '500ms' }}></div>
              <div className={`absolute top-0 right-0 w-10 h-10 border-t border-r border-amber-500/25 rounded-tr-xl transition-all duration-[1000ms] ${ctaVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}`} style={{ transitionDelay: '650ms' }}></div>
              <div className={`absolute bottom-0 left-0 w-10 h-10 border-b border-l border-amber-500/25 rounded-bl-xl transition-all duration-[1000ms] ${ctaVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}`} style={{ transitionDelay: '650ms' }}></div>
              <div className={`absolute bottom-0 right-0 w-10 h-10 border-b border-r border-amber-500/25 rounded-br-xl transition-all duration-[1000ms] ${ctaVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}`} style={{ transitionDelay: '800ms' }}></div>
            </div>

            <div className="relative bg-gradient-to-br from-[#1a1a1a] via-[#1e1c18] to-[#1a1a1a] rounded-3xl overflow-hidden border border-white/[0.04]">
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-[0.025]" style={{
                backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(217,169,78,0.8) 1px, transparent 0)',
                backgroundSize: '40px 40px'
              }}></div>
              
              {/* Top Glow Line */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-2/3 h-px bg-gradient-to-r from-transparent via-amber-500/40 to-transparent"></div>
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/3 h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent blur-[1px]"></div>
              
              {/* Ambient Corner Glows */}
              <div className="absolute top-0 left-0 w-64 h-64 bg-amber-500/[0.03] rounded-full blur-[80px]"></div>
              <div className="absolute bottom-0 right-0 w-64 h-64 bg-amber-500/[0.03] rounded-full blur-[80px]"></div>

              {/* Content */}
              <div className="relative px-12 md:px-16 pt-14 pb-12">
                {/* Top Label */}
                <div className={`flex items-center justify-center gap-3 mb-10 transition-all duration-[1200ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: '400ms' }}>
                  <div className="w-8 h-px bg-gradient-to-r from-transparent to-amber-500/40"></div>
                  <span className="text-[10px] tracking-[0.35em] text-amber-500/50 uppercase font-mono">Exclusive Opportunity</span>
                  <div className="w-8 h-px bg-gradient-to-l from-transparent to-amber-500/40"></div>
                </div>

                {/* Main Content */}
                <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16">
                  {/* Left - Text */}
                  <div className={`flex-1 text-center lg:text-left transition-all duration-[1300ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${ctaVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`} style={{ transitionDelay: '550ms' }}>
                    <h3 className="text-3xl md:text-4xl font-light text-white mb-4 leading-snug font-display">
                      Ready to Own This<br />
                      <span className="bg-gradient-to-r from-amber-300 via-amber-400 to-amber-500 bg-clip-text text-transparent">Premium Digital Asset</span>?
                    </h3>
                    <p className="text-white/40 text-sm leading-relaxed max-w-md mx-auto lg:mx-0 font-body">
                      Serious inquiries only. This domain represents a rare, high-value digital investment opportunity with lasting appreciation potential.
                    </p>
                  </div>

                  {/* Center Divider - Desktop */}
                  <div className="hidden lg:flex flex-col items-center gap-2 self-stretch py-2">
                    <div className="flex-1 w-px bg-gradient-to-b from-transparent via-amber-500/20 to-transparent"></div>
                    <div className="w-1.5 h-1.5 rotate-45 border border-amber-500/30"></div>
                    <div className="flex-1 w-px bg-gradient-to-b from-transparent via-amber-500/20 to-transparent"></div>
                  </div>

                  {/* Right - CTA */}
                  <div className={`flex flex-col items-center lg:items-end gap-6 transition-all duration-[1300ms] ease-[cubic-bezier(0.22,1,0.36,1)] ${ctaVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`} style={{ transitionDelay: '700ms' }}>
                    <button
                      onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                      className="group relative flex items-center gap-4 px-10 py-4 rounded-full cursor-pointer whitespace-nowrap overflow-hidden"
                    >
                      {/* Button BG */}
                      <div className="absolute inset-0 bg-gradient-to-r from-amber-500 via-amber-400 to-amber-500 group-hover:from-amber-400 group-hover:via-amber-300 group-hover:to-amber-400 transition-all duration-500"></div>
                      {/* Shine Sweep */}
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                      <span className="relative text-sm tracking-[0.15em] text-[#1a1a1a] font-medium uppercase font-mono">Contact Owner</span>
                      <i className="ri-arrow-right-line relative text-[#1a1a1a] group-hover:translate-x-1 transition-transform"></i>
                    </button>

                    {/* Trust Indicators */}
                    <div className="flex items-center gap-5">
                      <div className="flex items-center gap-1.5">
                        <i className="ri-shield-check-line text-xs text-amber-500/40"></i>
                        <span className="text-[10px] tracking-wider text-white/30 uppercase font-mono">Verified</span>
                      </div>
                      <div className="w-px h-3 bg-white/10"></div>
                      <div className="flex items-center gap-1.5">
                        <i className="ri-lock-line text-xs text-amber-500/40"></i>
                        <span className="text-[10px] tracking-wider text-white/30 uppercase font-mono">Secure Transfer</span>
                      </div>
                      <div className="w-px h-3 bg-white/10"></div>
                      <div className="flex items-center gap-1.5">
                        <i className="ri-time-line text-xs text-amber-500/40"></i>
                        <span className="text-[10px] tracking-wider text-white/30 uppercase font-mono">Instant</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bottom Glow Line */}
                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1/2 h-px bg-gradient-to-r from-transparent via-amber-500/20 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
